import { Button, Skeleton, cn } from '@eureca/ui';
import { ConsultantsMenu } from '@/shared/consultants-menu/ConsultantsMenu';
import { RouteIcon, TriangleAlertIcon } from './icons';

/**
 * Layout shell shared by the Jobs states rendered OUTSIDE the container (the
 * error boundary). Mirrors `CompaniesShell` so the surrounding navigation stays
 * identical across "/" and "/vagas".
 */
function JobsShell({ children, onLogout }: { children: React.ReactNode; onLogout?: () => void }) {
  return (
    <div className="flex min-h-dvh flex-row bg-muted">
      <ConsultantsMenu activeId="jobs" onLogout={onLogout} />
      <main className="flex flex-1 flex-col gap-8 px-4 pt-6 pb-16 md:px-8 md:pt-8">
        <header className="flex flex-row items-center gap-3">
          <div className="flex size-8 items-center justify-center rounded-md bg-secondary text-secondary-foreground">
            <RouteIcon className="size-4" />
          </div>
          <h1 className="text-3xl font-medium leading-none tracking-tight text-foreground md:text-4xl">
            Vagas
          </h1>
        </header>
        {children}
      </main>
    </div>
  );
}

/**
 * Skeleton of the jobs TABLE (no shell). Layout mirrors the loaded table so
 * paint frames don't shift content when results arrive.
 *
 * DEV-315 / VPG-011 (retorno da QA): a listagem renderiza este esqueleto SÓ na
 * área da tabela — a barra de filtros (Programa, Status, busca) segue montada e
 * interativa enquanto as vagas carregam, então dá para trocar de programa sem
 * esperar a resposta. Antes o container trocava a página inteira pelo
 * `JobsLoadingFallback` e, com rede lenta, a pessoa ficava presa.
 */
export function JobsListSkeleton() {
  return (
    <section
      aria-label="Carregando vagas"
      aria-busy="true"
      data-testid="recruiter-jobs-loading"
      className={cn(
        'overflow-hidden rounded-lg border border-border bg-card',
        'shadow-[0_1px_2px_-1px_rgba(33,41,40,0.05),0_1px_4px_-1px_rgba(33,41,40,0.05)]'
      )}
    >
      <div className="flex items-center gap-6 border-b border-border px-6 py-3">
        <Skeleton className="h-3 w-10" />
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-3 w-32" />
        <Skeleton className="h-3 w-20" />
      </div>
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center justify-between gap-6 border-b border-border px-6 py-4 last:border-b-0"
        >
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-4 max-w-[40%] flex-1" />
          <Skeleton shape="pill" className="h-6 w-32" />
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </section>
  );
}

export interface JobsErrorFallbackProps {
  onRetry: () => void;
  onLogout?: () => void;
  /** Optional human-readable message; surfaced to support if shown to the user. */
  message?: string;
}

export function JobsErrorFallback({ onRetry, onLogout, message }: JobsErrorFallbackProps) {
  return (
    <JobsShell onLogout={onLogout}>
      <div role="alert" className="mt-16 flex w-full flex-col items-center gap-3">
        <div className="flex w-full flex-col items-center gap-5">
          <div
            aria-hidden="true"
            className="flex h-16 w-16 items-center justify-center rounded-full bg-muted"
          >
            <TriangleAlertIcon className="h-6 w-6 text-foreground" />
          </div>
          <div className="flex w-full flex-col items-center gap-2 text-center">
            <h2 className="text-2xl font-semibold leading-none tracking-tight text-foreground">
              Não foi possível carregar as vagas
            </h2>
            <p className="text-base leading-normal text-muted-foreground">
              {message ??
                'Houve um problema ao buscar as vagas. Tente novamente em alguns instantes.'}
            </p>
          </div>
        </div>
        <div className="flex flex-row items-center justify-center gap-2 self-stretch">
          <Button
            type="button"
            size="lg"
            onClick={onRetry}
            data-testid="recruiter-jobs-error-retry"
          >
            Tentar novamente
          </Button>
        </div>
      </div>
    </JobsShell>
  );
}
